import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    placeholders: Array,
  };

  static targets = [
    "sectionSelector",
    "titleInput",
    "widthSelector",
    "childrenContainer",
    "headerTitle",
  ];

  connect() {
    this.defineTargets();
    this.updateContentSection();
  }

  defineTargets() {
    this.placeholdersValue.forEach((value) => {
      this[`${value}Target`] = this.element.querySelector(
        `[data-forms--content-sections-target="${value}"]`,
      );
    });
  }

  hasTarget(placeholder) {
    return !!this[`${placeholder}Target`];
  }

  updateContentSection() {
    this.hideAllSections();
    const selectedOption = this.sectionSelectorTarget.selectedOptions[0];
    const placeholders = selectedOption.dataset.sectionPlaceholders.split(" ");
    placeholders.forEach((placeholder) => {
      if (placeholder.length && this.hasTarget(placeholder)) {
        this[`${placeholder}Target`].classList.remove("d-none");
      }
    });

    // Update container status for nested sortable
    this.updateContainerStatus(selectedOption);

    // Update the accordion header to reflect the current section type
    this.updateAccordionHeader();
  }

  hideAllSections() {
    this.placeholdersValue.forEach((placeholder) => {
      if (this.hasTarget(placeholder)) {
        this[`${placeholder}Target`].classList.add("d-none");
      }
    });
  }

  /**
   * Updates the accordion header text. If the user has entered a custom
   * title, that is displayed. Otherwise a description is generated from
   * the selected section type and width (e.g. "Full width Rich text").
   *
   * Called on title input, section type change, and width change.
   */
  updateAccordionHeader() {
    if (!this.hasHeaderTitleTarget) return;

    const customTitle = this.hasTitleInputTarget
      ? this.titleInputTarget.value.trim()
      : "";

    if (customTitle) {
      this.headerTitleTarget.textContent = customTitle;
    } else {
      this.headerTitleTarget.textContent = this.sectionDescription();
    }
  }

  /**
   * Generates a description from the selected section type and width,
   * matching the Ruby model format: "{width} width {type}" capitalized.
   */
  sectionDescription() {
    const sectionName = this.selectedSectionName();
    const widthLabel = this.selectedWidthLabel();

    if (sectionName && widthLabel) {
      const desc = `${widthLabel} width ${sectionName}`;
      return desc.charAt(0).toUpperCase() + desc.slice(1);
    }
    return sectionName || "";
  }

  selectedSectionName() {
    if (!this.hasSectionSelectorTarget) return null;
    const option = this.sectionSelectorTarget.selectedOptions[0];
    return option ? option.text.trim() : null;
  }

  selectedWidthLabel() {
    if (!this.hasWidthSelectorTarget) return null;
    const option = this.widthSelectorTarget.selectedOptions[0];
    return option ? option.text.trim().toLowerCase() : null;
  }

  /**
   * Updates the parent item's allows-children data attribute when the section template changes.
   * Shows/hides the children container with the "Add child section" button.
   *
   * When the section type allows children but the record hasn't been saved yet,
   * the container is shown with a disabled button and a prompt to save first.
   */
  updateContainerStatus(selectedOption) {
    const allowsChildren = selectedOption?.dataset.allowsChildren === "true";

    const item = this.element.closest(
      '[data-forms--nested-sortable-target="item"]',
    );
    if (!item) return;

    const isChild = this.isChildSection(item);
    const isPersisted = this.isPersistedSection(item);
    const showContainer = allowsChildren && !isChild;
    const canAddChildren = showContainer && isPersisted;

    item.dataset.allowsChildren = canAddChildren.toString();

    // Show/hide the children container
    if (this.hasChildrenContainerTarget) {
      this.childrenContainerTarget.classList.toggle("d-none", !showContainer);

      // Toggle the save prompt and button disabled state
      const savePrompt =
        this.childrenContainerTarget.querySelector(".save-prompt");
      if (savePrompt) {
        savePrompt.classList.toggle("d-none", canAddChildren);
      }

      const addLink = this.childrenContainerTarget.querySelector(
        ":scope > .children-add-link",
      );
      if (addLink) {
        addLink.classList.toggle("disabled", !canAddChildren);
      }
    }

    // Update the Container badge in the header
    const header = item.querySelector(".accordion-button");
    if (header) {
      let badge = header.querySelector(".badge.bg-info");
      if (showContainer && !badge) {
        badge = document.createElement("span");
        badge.className = "badge bg-info ms-2";
        badge.textContent = "Container";
        header.appendChild(badge);
      } else if (!showContainer && badge) {
        badge.remove();
      }
    }
  }

  /**
   * Returns true if this section is a child (nested inside a parent container).
   * Checks the nu_parent_id hidden field value.
   */
  isChildSection(item) {
    const parentInput = item.querySelector(
      '[data-forms--nested-sortable-target="parentInput"]',
    );
    return parentInput && parentInput.value !== "";
  }

  /**
   * Returns true if this section has been persisted (has a database id).
   */
  isPersistedSection(item) {
    const idInput = item.querySelector('input[name*="[id]"]');
    return idInput && idInput.value !== "";
  }

  /**
   * Adds a new child section inside this container section.
   * Stamps a new section from the page-level template, sets its nu_parent_id,
   * marks it as a child, and inserts it into the children container.
   */
  addChild(event) {
    event.preventDefault();

    const item = this.element.closest(
      '[data-forms--nested-sortable-target="item"]',
    );
    if (!item) return;

    const parentId = this.parentSectionId(item);
    if (!parentId) return;

    // Find the page-level template (used by forms--nested-attrs for root sections)
    const template = document.querySelector(
      '[data-forms--nested-attrs-target="template"]',
    );
    if (!template) return;

    // Stamp a new section from the template with a unique index
    const newIndex = new Date().getTime();
    const content = template.innerHTML.replace(/NEW_RECORD/g, newIndex);

    // Parse the HTML and configure it as a child
    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    const newItem = wrapper.firstElementChild;

    if (!newItem) return;

    // Set the parent_id on the new child
    const parentInput = newItem.querySelector(
      '[data-forms--nested-sortable-target="parentInput"]',
    );
    if (parentInput) parentInput.value = parentId;

    // Add child visual styling
    newItem.classList.add("nested-child");

    // Add child badge after the section title in the accordion header
    const titleSpan = newItem.querySelector(".section-title");
    if (titleSpan) {
      const badge = document.createElement("span");
      badge.className = "badge bg-secondary ms-2";
      badge.textContent = "Child";
      titleSpan.after(badge);
    }

    // Hide the children container on the new child (children can't have children)
    const childContainer = newItem.querySelector(".children-container");
    if (childContainer) childContainer.classList.add("d-none");

    // Hide the width control: it has no effect on children (the parent template
    // controls their grid slot). The select stays in the DOM so its value is
    // still submitted and satisfies the presence validation.
    const widthSelector = newItem.querySelector(
      '[data-forms--content-sections-target="widthSelector"]',
    );
    const widthRow = widthSelector?.closest(".row");
    if (widthRow) widthRow.classList.add("d-none");

    // Disable accordion parent behaviour so child sections can be opened independently
    const collapse = newItem.querySelector(".accordion-collapse");
    if (collapse) collapse.removeAttribute("data-bs-parent");

    // Insert into the children container, before the add button.
    // Use :scope to target only the direct child link, not links inside
    // nested child sections that have their own (hidden) children containers.
    const addButton = this.hasChildrenContainerTarget
      ? this.childrenContainerTarget.querySelector(
          ":scope > .children-add-link",
        )
      : null;
    if (addButton) {
      this.childrenContainerTarget.insertBefore(newItem, addButton);
    } else if (this.hasChildrenContainerTarget) {
      this.childrenContainerTarget.appendChild(newItem);
    }
  }

  /**
   * Returns the persisted section ID for use as a parent reference.
   * Children can only be added to persisted sections, so the
   * data-parent-section-id attribute is always set from the record's id.
   */
  parentSectionId(item) {
    const childrenContainer = item.querySelector(".children-container");
    return childrenContainer?.dataset.parentSectionId || null;
  }
}
